import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { loginUnified, sendOtpUnified, verifyOtpUnified, setDemoPassword } from '../../services/authService';
import { changeDemoPassword } from '../../services/demoService';

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();

  // Check URL params for account=demo or demo=true
  const queryParams = new URLSearchParams(location.search);
  const initialIsDemo = queryParams.get('account') === 'demo' || queryParams.get('demo') === 'true';

  // Form States
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isDemoStudent, setIsDemoStudent] = useState(initialIsDemo);
  const [adminPhone, setAdminPhone] = useState('919548354084');

  // Auth Modes: 'password' | 'otp' | 'create_password' | 'change_temp_password'
  const [authMode, setAuthMode] = useState('password');
  
  // OTP States (Email-Only)
  const [otpEmail, setOtpEmail] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [otpStep, setOtpStep] = useState(1); // 1 = Request, 2 = Verify
  const [otpCode, setOtpCode] = useState('');
  const [timer, setTimer] = useState(0);
  const [activeOtpIsDemo, setActiveOtpIsDemo] = useState(initialIsDemo);

  // Demo Password Creation & Forced Change States
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentTempPassword, setCurrentTempPassword] = useState('');

  // UI Feedback States
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [statusType, setStatusType] = useState('');
  const [demoDetails, setDemoDetails] = useState({
    code: '',
    status: '',
    student_name: '',
    student_email: ''
  });

  const errorRef = React.useRef(null);

  const getLogoUrl = () => {
    const apiBase = import.meta.env.VITE_API_BASE_URL || '/my-lms/backend/api';
    return `${apiBase}/logo.php`;
  };

  // Safe Email Masking Helper (e.g., a*****k@example.com)
  const maskEmailAddress = (emailStr) => {
    if (!emailStr || !emailStr.includes('@')) return emailStr;
    const [name, domain] = emailStr.split('@');
    const len = name.length;
    if (len <= 2) return `${name.slice(0, 1)}*@${domain}`;
    const maskedName = `${name.slice(0, 1)}${'*'.repeat(Math.min(5, len - 2))}${name.slice(-1)}`;
    return `${maskedName}@${domain}`;
  };

  // Redirect authenticated users to appropriate dashboard (or prompt password change if required)
  useEffect(() => {
    if (user && !user.must_change_password) {
      if (user.role === 'demo_student') {
        navigate('/demo/dashboard');
      } else {
        navigate(`/${user.role}/dashboard`);
      }
    } else if (user && user.must_change_password === 1) {
      setAuthMode('change_temp_password');
      setSuccessMsg('Your temporary password must be changed before accessing the demo dashboard.');
    }
  }, [user, navigate]);

  // Handle location state for error messages from redirects
  useEffect(() => {
    if (location.state && location.state.error) {
      setErrorMsg(location.state.error);
      if (location.state.status) {
        setStatusType(location.state.status);
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Countdown timer for OTP resend
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(t => t - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getWhatsAppSupportLink = (statusKey, studentName, studentEmail) => {
    let formattedStatus = statusKey || 'non-active';
    if (statusKey) {
      formattedStatus = statusKey.charAt(0).toUpperCase() + statusKey.slice(1);
    }
    
    const safeEmail = studentEmail || (identifier.includes('@') ? identifier.trim() : '');
    let text = `Hello PranaFlow Academy, I am unable to access my demo-class dashboard.`;
    if (safeEmail) {
      text += ` My registered email is ${safeEmail} and my account status is ${formattedStatus}.`;
    } else {
      text += ` My account status is ${formattedStatus}.`;
    }
    text += ` Please help me resolve this issue.`;
    
    return `https://wa.me/919548354084?text=${encodeURIComponent(text)}`;
  };

  const handleDemoCheckboxChange = (e) => {
    setIsDemoStudent(e.target.checked);
    setErrorMsg('');
    setSuccessMsg('');
    setStatusType('');
    setDemoDetails({ code: '', status: '', heading: '', student_name: '', student_email: '' });
  };

  // Handle Password Submit (Supports email or mobile number)
  const handlePasswordSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (loading) return;

    setErrorMsg('');
    setSuccessMsg('');
    setStatusType('');

    const val = identifier.trim();
    if (!val) {
      setErrorMsg('Enter your registered email address or WhatsApp number.');
      return;
    }
    if (!password) {
      setErrorMsg('Please enter your password.');
      return;
    }

    setLoading(true);
    try {
      const res = await loginUnified({
        email: val,
        identifier: val,
        password,
        is_demo: isDemoStudent
      });

      const loggedUser = res;
      setUser(loggedUser);
      if (loggedUser && loggedUser.token) {
        localStorage.setItem('token', loggedUser.token);
      }

      if (loggedUser.must_change_password === 1) {
        setCurrentTempPassword(password);
        setAuthMode('change_temp_password');
        setSuccessMsg('Your temporary password must be changed before accessing the demo dashboard.');
      } else if (loggedUser.role === 'demo_student') {
        navigate('/demo/dashboard');
      } else {
        navigate(`/${loggedUser.role}/dashboard`);
      }
    } catch (err) {
      setPassword('');
      let displayError = err.message || 'Invalid login details.';
      if (err.errors && typeof err.errors === 'object') {
        const firstErr = Object.values(err.errors)[0];
        if (firstErr && typeof firstErr === 'string') {
          displayError = firstErr;
        }
      }
      setErrorMsg(displayError);
      if (err.code) {
        setStatusType(err.code);
      }
      setDemoDetails({
        code: err.code || '',
        status: err.status || '',
        heading: err.heading || '',
        student_name: err.student_name || '',
        student_email: err.student_email || (identifier.includes('@') ? identifier.trim() : '')
      });
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 50);
    } finally {
      setLoading(false);
    }
  };

  // Switch to OTP Mode
  const switchToOtpMode = () => {
    setAuthMode('otp');
    setOtpStep(1);
    setErrorMsg('');
    setSuccessMsg('');
    setStatusType('');
    // Pre-fill email if identifier was already typed and contains @
    if (identifier.includes('@')) {
      setOtpEmail(identifier.trim());
    } else {
      setOtpEmail('');
    }
  };

  // Handle Request Email OTP
  const handleRequestOtp = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (loading) return;
    setErrorMsg('');
    setSuccessMsg('');
    setStatusType('');

    const emailVal = otpEmail.trim();

    if (!emailVal) {
      setErrorMsg('Enter a valid registered email address.');
      return;
    }

    // Client Pre-validation: Reject mobile/WhatsApp numbers in OTP mode
    if (!emailVal.includes('@') || !/\S+@\S+\.\S+/.test(emailVal)) {
      setErrorMsg('OTP sign-in is available through email only. Please enter the email address registered with your student account.');
      return;
    }

    setLoading(true);
    const selectedDemoMode = isDemoStudent;
    setActiveOtpIsDemo(selectedDemoMode);

    try {
      const res = await sendOtpUnified({
        email: emailVal,
        identifier: emailVal,
        is_demo: selectedDemoMode,
        login_type: selectedDemoMode ? 'demo_student' : 'regular_student'
      });

      const masked = res.masked_email || maskEmailAddress(emailVal);
      setMaskedEmail(masked);
      setOtpStep(2);
      setTimer(60);
      setSuccessMsg(res.message || 'A verification code has been sent to your registered email address.');
    } catch (err) {
      setErrorMsg(err.message || 'Failed to dispatch verification code.');
      if (err.code) {
        setStatusType(err.code);
      }
      setDemoDetails({
        code: err.code || '',
        status: err.status || '',
        heading: err.heading || '',
        student_name: err.student_name || '',
        student_email: err.student_email || emailVal
      });
      setTimeout(() => {
        if (errorRef.current) {
          errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 50);
    } finally {
      setLoading(false);
    }
  };

  // Handle Verify Email OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    setStatusType('');

    if (otpCode.length !== 6) {
      setErrorMsg('The OTP you entered is incorrect. Please check the code and try again.');
      return;
    }

    setLoading(true);
    const emailVal = otpEmail.trim();

    try {
      const userData = await verifyOtpUnified({
        email: emailVal,
        identifier: emailVal,
        otp: otpCode,
        is_demo: activeOtpIsDemo,
        login_type: activeOtpIsDemo ? 'demo_student' : 'regular_student'
      });

      if (userData.needs_password) {
        setAuthMode('create_password');
        setSuccessMsg('Verification successful. Please create a password for faster access to your demo classes.');
      } else {
        setUser(userData);
        if (userData && userData.token) {
          localStorage.setItem('token', userData.token);
        }

        if (userData.must_change_password === 1) {
          setAuthMode('change_temp_password');
          setSuccessMsg('Your temporary password must be changed before accessing the demo dashboard.');
        } else if (userData.role === 'demo_student') {
          navigate('/demo/dashboard');
        } else {
          navigate(`/${userData.role}/dashboard`);
        }
      }
    } catch (err) {
      setErrorMsg(err.message || 'The OTP you entered is incorrect. Please check the code and try again.');
      if (err.code) {
        setStatusType(err.code);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Resend Email OTP
  const handleResendOtp = async () => {
    if (timer > 0) return;
    setErrorMsg('');
    setSuccessMsg('');
    setStatusType('');
    setLoading(true);

    const emailVal = otpEmail.trim();

    try {
      const res = await sendOtpUnified({
        email: emailVal,
        identifier: emailVal,
        is_demo: activeOtpIsDemo,
        login_type: activeOtpIsDemo ? 'demo_student' : 'regular_student'
      });
      setTimer(60);
      setSuccessMsg(res.message || 'A new verification code has been sent to your registered email address.');
    } catch (err) {
      setErrorMsg(err.message || 'Failed to resend code.');
      if (err.code) {
        setStatusType(err.code);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle Post-OTP Password Creation
  const handleCreatePasswordSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!newPassword) {
      setErrorMsg('Please enter a password.');
      return;
    }
    if (newPassword.length < 8) {
      setErrorMsg('Enter at least eight characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('The passwords do not match.');
      return;
    }

    setLoading(true);
    const emailVal = otpEmail.trim();

    try {
      const userData = await setDemoPassword({
        value: emailVal,
        password: newPassword,
        confirm_password: confirmPassword
      });

      setUser(userData);
      if (userData && userData.token) {
        localStorage.setItem('token', userData.token);
      }

      navigate('/demo/dashboard');
    } catch (err) {
      setErrorMsg(err.message || 'Failed to create password.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Forced Temporary Password Change
  const handleChangeTempPasswordSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');

    if (!newPassword) {
      setErrorMsg('Please enter your new password.');
      return;
    }
    if (newPassword.length < 8) {
      setErrorMsg('Enter at least eight characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setErrorMsg('The passwords do not match.');
      return;
    }

    setLoading(true);

    try {
      const updatedUser = await changeDemoPassword({
        current_password: currentTempPassword,
        new_password: newPassword,
        confirm_new_password: confirmPassword
      });

      setUser(updatedUser);
      if (updatedUser && updatedUser.token) {
        localStorage.setItem('token', updatedUser.token);
      }

      navigate('/demo/dashboard');
    } catch (err) {
      setErrorMsg(err.message || 'Failed to update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-body" style={{ display: 'flex', minHeight: '100dvh', justifyContent: 'center', alignItems: 'center', backgroundColor: 'var(--bg-base)', padding: '16px' }}>
      <div className="login-card-wrapper" style={{ width: '100%', maxWidth: '460px' }}>
        <div className="login-card" style={{ padding: '32px', backgroundColor: '#ffffff', borderRadius: '16px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.08), 0 8px 10px -6px rgba(0,0,0,0.01)', border: '1px solid #e5e7eb' }}>
          
          {/* Logo */}
          <div className="login-logo" style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
            <img src={getLogoUrl()} className="brand-logo" alt="PranaFlow Academy" style={{ display: 'block', margin: '0 auto', maxHeight: '48px' }} />
            <span style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--primary, #0D443E)', textAlign: 'center' }}>PranaFlow Portal</span>
          </div>

          {/* Heading */}
          <h2 className="login-title" style={{ textAlign: 'center', fontSize: '1.5rem', fontWeight: 800, color: '#111827', margin: '0 0 4px 0' }}>
            {authMode === 'create_password' 
              ? 'Create a Password' 
              : (authMode === 'change_temp_password' 
                ? 'Update Password' 
                : 'Sign in to your student account')}
          </h2>
          
          <p style={{ textAlign: 'center', fontSize: '0.85rem', color: '#6b7280', margin: '0 0 20px 0', lineHeight: 1.45 }}>
            {authMode === 'create_password'
              ? 'Create a password for faster access to your demo classes.'
              : (authMode === 'change_temp_password'
                ? 'Your administrator required you to set a new password before accessing your dashboard.'
                : (authMode === 'otp'
                  ? 'Enter your registered email address to receive a one-time verification code.'
                  : (isDemoStudent
                    ? 'Sign in to access your registered demo classes.'
                    : 'Use your registered email address or WhatsApp number to continue.')))}
          </p>

          {/* Error Feedback Card */}
          {errorMsg && (
            <div 
              ref={errorRef}
              style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '16px', borderRadius: '12px', fontSize: '0.88rem', marginBottom: '20px' }}
            >
              {/* Title */}
              {(statusType === 'DEMO_ACCOUNT_NOT_FOUND' || statusType === 'DEMO_STUDENT_NOT_FOUND' || statusType === 'STUDENT_DELETED') ? (
                <div style={{ fontWeight: 800, fontSize: '1rem', color: '#991b1b', marginBottom: '6px' }}>
                  Demo account not found
                </div>
              ) : (statusType === 'DEMO_ACCOUNT_NOT_ACTIVE' || statusType === 'STUDENT_PENDING' || statusType === 'STUDENT_SUSPENDED' || statusType === 'DEMO_BATCH_INACTIVE') ? (
                <div style={{ fontWeight: 800, fontSize: '1rem', color: '#991b1b', marginBottom: '6px' }}>
                  {demoDetails.status === 'pending' || statusType === 'STUDENT_PENDING'
                    ? 'Demo account awaiting approval'
                    : (demoDetails.status === 'inactive' || statusType === 'DEMO_BATCH_INACTIVE'
                      ? 'Demo account is inactive'
                      : (demoDetails.status === 'suspended' || demoDetails.status === 'blocked' || statusType === 'STUDENT_SUSPENDED'
                        ? 'Demo account access suspended'
                        : (demoDetails.status === 'rejected'
                          ? 'Demo-class access was not approved'
                          : (demoDetails.status === 'expired'
                            ? 'Demo-class access has expired'
                            : (demoDetails.status === 'converted'
                              ? 'Full Enrollment Activated'
                              : 'Demo dashboard access unavailable')))))}
                </div>
              ) : null}

              <div style={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}>{errorMsg}</div>

              {/* Actions for DEMO_ACCOUNT_NOT_FOUND */}
              {(statusType === 'DEMO_ACCOUNT_NOT_FOUND' || statusType === 'DEMO_STUDENT_NOT_FOUND' || statusType === 'STUDENT_DELETED') && (
                <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                  <button 
                    type="button"
                    className="btn btn-primary"
                    style={{ flex: 1, padding: '9px 12px', fontSize: '0.82rem', fontWeight: 700, borderRadius: '8px', backgroundColor: 'var(--primary, #0D443E)', color: '#fff', border: 'none', cursor: 'pointer' }}
                    onClick={() => navigate('/courses')}
                  >
                    Register for Demo
                  </button>
                  <button 
                    type="button"
                    className="btn btn-secondary"
                    style={{ flex: 1, padding: '9px 12px', fontSize: '0.82rem', fontWeight: 700, borderRadius: '8px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', cursor: 'pointer' }}
                    onClick={() => navigate('/courses')}
                  >
                    Explore Courses
                  </button>
                </div>
              )}

              {/* Actions for Converted Status */}
              {demoDetails.status === 'converted' && (
                <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                  <button 
                    type="button"
                    className="btn btn-primary"
                    style={{ flex: 1, padding: '9px 12px', fontSize: '0.82rem', fontWeight: 700, borderRadius: '8px', backgroundColor: 'var(--primary, #0D443E)', color: '#fff', border: 'none', cursor: 'pointer' }}
                    onClick={() => { setIsDemoStudent(false); setErrorMsg(''); setStatusType(''); }}
                  >
                    Student Login
                  </button>
                  <button 
                    type="button"
                    className="btn btn-secondary"
                    style={{ flex: 1, padding: '9px 12px', fontSize: '0.82rem', fontWeight: 700, borderRadius: '8px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', cursor: 'pointer' }}
                    onClick={() => navigate('/courses')}
                  >
                    Explore Courses
                  </button>
                </div>
              )}

              {/* WhatsApp Support Button for non-active demo account statuses */}
              {(statusType === 'DEMO_ACCOUNT_NOT_ACTIVE' || statusType === 'STUDENT_PENDING' || statusType === 'STUDENT_SUSPENDED' || statusType === 'DEMO_BATCH_INACTIVE') && demoDetails.status !== 'converted' && (
                <div style={{ marginTop: '14px' }}>
                  <a
                    href={getWhatsAppSupportLink(demoDetails.status || statusType, demoDetails.student_name, demoDetails.student_email)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: '8px', 
                      width: '100%', 
                      padding: '10px 14px', 
                      fontSize: '0.88rem', 
                      fontWeight: 700, 
                      backgroundColor: '#25D366', 
                      color: '#fff', 
                      border: 'none', 
                      borderRadius: '8px', 
                      textDecoration: 'none',
                      boxShadow: '0 3px 6px rgba(0,0,0,0.1)',
                      boxSizing: 'border-box'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.031 6.172c-3.181 0-5.767 2.586-5.768 5.766-.001 1.298.424 2.5 1.134 3.473L6.5 20.5l5.241-1.077c.905.474 1.933.745 3.023.745 3.18 0 5.765-2.586 5.767-5.766 0-3.18-2.585-5.766-5.767-5.766zm3.336 8.3c-.145.41-.734.757-1.12.825-.333.058-.77.106-2.29-.523-1.942-.803-3.18-2.775-3.277-2.905-.098-.13-1.12-1.488-1.12-2.84 0-1.35.706-2.014.957-2.274.25-.26.549-.327.733-.327.184 0 .367.001.528.009.167.008.391-.065.612.464.22.529.756 1.84.82 1.97.065.13.11.282.023.456-.086.174-.13.282-.259.432-.13.15-.27.336-.386.45-.13.13-.266.272-.115.53.15.258.665 1.096 1.428 1.774.985.874 1.815 1.144 2.072 1.272.258.128.41.107.561-.068.151-.174.651-.757.825-1.016.174-.258.348-.217.587-.13.239.088 1.517.715 1.777.846.26.13.434.195.495.3.061.105.061.61-.084 1.02zM12 2C6.477 2 2 6.477 2 12c0 2.01.593 3.88 1.613 5.441L2 22l4.733-1.54C8.243 21.463 10.063 22 12 22c5.523 0 10-4.477 10-10S17.523 2 12 2zm0 18c-1.634 0-3.167-.488-4.46-1.326l-.32-.205-2.793.908.924-2.724-.225-.357C4.28 15.02 3.8 13.563 3.8 12c0-4.522 3.678-8.2 8.2-8.2 4.522 0 8.2 3.678 8.2 8.2 0 4.522-3.678 8.2-8.2 8.2z" />
                    </svg>
                    Message Admin on WhatsApp
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Success Feedback Card */}
          {successMsg && (
            <div style={{ backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', padding: '12px 14px', borderRadius: '10px', fontSize: '0.85rem', marginBottom: '20px', lineHeight: 1.45 }}>
              {successMsg}
            </div>
          )}

          {/* MODE 1: Password Login (Supports Email or Mobile Number) */}
          {authMode === 'password' && (
            <form onSubmit={handlePasswordSubmit}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label htmlFor="loginIdentifier" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                  Email Address or WhatsApp Number
                </label>
                <input
                  id="loginIdentifier"
                  type="text"
                  placeholder="Enter registered email or mobile number"
                  value={identifier}
                  onChange={e => setIdentifier(e.target.value)}
                  required
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label htmlFor="loginPassword" style={{ fontSize: '0.85rem', fontWeight: 700, color: '#374151', margin: 0 }}>
                    Password
                  </label>
                  {!isDemoStudent && (
                    <span 
                      onClick={() => navigate('/forgot-password')} 
                      style={{ fontSize: '0.8rem', color: 'var(--primary, #0D443E)', fontWeight: 700, cursor: 'pointer', textDecoration: 'none' }}
                    >
                      Forgot Password?
                    </span>
                  )}
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    id="loginPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    style={{ width: '100%', padding: '12px 40px 12px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: 0 }}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? (
                      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.908a10.016 10.016 0 013.682-.813c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21M3 3l18 18"/></svg>
                    ) : (
                      <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Action link to switch to OTP */}
              <div style={{ textAlign: 'right', marginBottom: '18px' }}>
                <button
                  type="button"
                  onClick={switchToOtpMode}
                  style={{ background: 'none', border: 'none', color: 'var(--primary, #0D443E)', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                >
                  Sign in with OTP
                </button>
              </div>

              <div style={{ marginBottom: '20px', padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <input
                  type="checkbox"
                  id="demoCheckboxPassword"
                  checked={isDemoStudent}
                  onChange={handleDemoCheckboxChange}
                  style={{ width: '18px', height: '18px', accentColor: 'var(--primary, #0D443E)', cursor: 'pointer' }}
                />
                <label htmlFor="demoCheckboxPassword" style={{ fontSize: '0.88rem', fontWeight: 600, color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
                  I’m enrolled as a demo class student
                </label>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ width: '100%', padding: '14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 700, backgroundColor: 'var(--primary, #0D443E)', color: '#fff' }}
              >
                {loading ? 'Signing in...' : 'Log In'}
              </button>
            </form>
          )}

          {/* MODE 2: Email OTP Login (Strictly Email-Only) */}
          {authMode === 'otp' && (
            <div>
              {otpStep === 1 ? (
                <form onSubmit={handleRequestOtp}>
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label htmlFor="otpEmailInput" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                      Registered Email Address
                    </label>
                    <input
                      id="otpEmailInput"
                      type="email"
                      placeholder="Enter your registered email address"
                      value={otpEmail}
                      onChange={e => setOtpEmail(e.target.value)}
                      required
                      style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div style={{ textAlign: 'right', marginBottom: '18px' }}>
                    <button
                      type="button"
                      onClick={() => { setAuthMode('password'); setErrorMsg(''); setSuccessMsg(''); setStatusType(''); }}
                      style={{ background: 'none', border: 'none', color: 'var(--primary, #0D443E)', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                    >
                      Sign in with password instead
                    </button>
                  </div>

                  <div style={{ marginBottom: '20px', padding: '10px 12px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input
                      type="checkbox"
                      id="demoCheckboxOtp"
                      checked={isDemoStudent}
                      onChange={handleDemoCheckboxChange}
                      style={{ width: '18px', height: '18px', accentColor: 'var(--primary, #0D443E)', cursor: 'pointer' }}
                    />
                    <label htmlFor="demoCheckboxOtp" style={{ fontSize: '0.88rem', fontWeight: 600, color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
                      I’m enrolled as a demo class student
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                    style={{ width: '100%', padding: '14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 700, backgroundColor: 'var(--primary, #0D443E)', color: '#fff' }}
                  >
                    {loading ? 'Sending OTP...' : 'Send OTP'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp}>
                  {/* Masked Email Indicator */}
                  {maskedEmail && (
                    <div style={{ textAlign: 'center', marginBottom: '14px', fontSize: '0.88rem', color: '#4b5563' }}>
                      Verification code sent to <strong style={{ color: '#111827' }}>{maskedEmail}</strong>
                    </div>
                  )}

                  <div className="form-group" style={{ marginBottom: '24px', textAlign: 'center' }}>
                    <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: 700, color: '#374151', marginBottom: '12px' }}>Enter 6-Digit Verification Code</label>
                    <input
                      type="text"
                      maxLength="6"
                      placeholder="------"
                      value={otpCode}
                      onChange={e => setOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                      required
                      style={{ width: '170px', letterSpacing: '8px', textAlign: 'center', padding: '12px', borderRadius: '8px', border: '2px solid var(--primary, #0D443E)', fontSize: '1.5rem', fontWeight: 'bold', outline: 'none', margin: '0 auto', display: 'block' }}
                    />
                  </div>

                  {/* Demo checkbox locked during verification step */}
                  <div style={{ marginBottom: '20px', padding: '10px 12px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', opacity: 0.8 }}>
                    <input
                      type="checkbox"
                      id="demoCheckboxOtpLocked"
                      checked={isDemoStudent}
                      disabled={true}
                      style={{ width: '18px', height: '18px', accentColor: 'var(--primary, #0D443E)', cursor: 'not-allowed' }}
                    />
                    <label htmlFor="demoCheckboxOtpLocked" style={{ fontSize: '0.88rem', fontWeight: 600, color: '#6b7280', cursor: 'not-allowed', userSelect: 'none' }}>
                      I’m enrolled as a demo class student
                    </label>
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                    style={{ width: '100%', padding: '14px', fontSize: '1rem', fontWeight: 700, cursor: 'pointer', marginBottom: '16px', borderRadius: '8px', backgroundColor: 'var(--primary, #0D443E)', color: '#fff', border: 'none' }}
                  >
                    {loading ? 'Verifying...' : 'Verify OTP'}
                  </button>

                  <div style={{ textAlign: 'center', fontSize: '0.88rem', color: '#6b7280', marginBottom: '16px' }}>
                    {timer > 0 ? (
                      <span>Resend OTP in {timer}s</span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={loading}
                        style={{ background: 'none', border: 'none', color: 'var(--primary, #0D443E)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                      >
                        Resend OTP
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() => { setOtpStep(1); setOtpCode(''); setErrorMsg(''); setSuccessMsg(''); setStatusType(''); }}
                    style={{ width: '100%', padding: '12px', fontSize: '0.9rem', fontWeight: 600, borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: '#374151', marginBottom: '12px' }}
                  >
                    Change email address
                  </button>

                  <div style={{ textAlign: 'center' }}>
                    <button
                      type="button"
                      onClick={() => { setAuthMode('password'); setErrorMsg(''); setSuccessMsg(''); setStatusType(''); }}
                      style={{ background: 'none', border: 'none', color: 'var(--primary, #0D443E)', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                    >
                      Sign in with password instead
                    </button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* MODE 3: Create Password (Post-OTP for Demo Students) */}
          {authMode === 'create_password' && (
            <form onSubmit={handleCreatePasswordSubmit}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label htmlFor="createPassword" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                  New Password
                </label>
                <input
                  id="createPassword"
                  type="password"
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="createConfirmPassword" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                  Confirm Password
                </label>
                <input
                  id="createConfirmPassword"
                  type="password"
                  placeholder="Re-enter your new password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ width: '100%', padding: '14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 700, backgroundColor: 'var(--primary, #0D443E)', color: '#fff' }}
              >
                {loading ? 'Creating Password...' : 'Save Password & Continue'}
              </button>
            </form>
          )}

          {/* MODE 4: Forced Password Change (must_change_password = 1) */}
          {authMode === 'change_temp_password' && (
            <form onSubmit={handleChangeTempPasswordSubmit}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label htmlFor="currentTempPwd" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                  Current Temporary Password
                </label>
                <input
                  id="currentTempPwd"
                  type="password"
                  placeholder="Enter current temporary password"
                  value={currentTempPassword}
                  onChange={e => setCurrentTempPassword(e.target.value)}
                  required
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label htmlFor="newForcedPwd" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                  New Password
                </label>
                <input
                  id="newForcedPwd"
                  type="password"
                  placeholder="At least 8 characters"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  required
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: '20px' }}>
                <label htmlFor="confirmForcedPwd" style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700, color: '#374151', marginBottom: '6px' }}>
                  Confirm New Password
                </label>
                <input
                  id="confirmForcedPwd"
                  type="password"
                  placeholder="Re-enter your new password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  required
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ width: '100%', padding: '14px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '1rem', fontWeight: 700, backgroundColor: 'var(--primary, #0D443E)', color: '#fff' }}
              >
                {loading ? 'Updating Password...' : 'Update Password & Access Dashboard'}
              </button>
            </form>
          )}

        </div>
      </div>
    </div>
  );
};

export default Login;
